// src/components/FarmerMyOrdersModal.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from '../../../context/AuthContext';

// --- Utility Functions ---
const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "KShs. 0";
    return `KShs. ${Number(amount).toLocaleString()}`;
};

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

// --- Enhanced Order Status Badge Component ---
const OrderStatusBadge = ({ status }) => {
    const statusConfig = {
        pending: { 
            color: "bg-amber-50 text-amber-800 border border-amber-200", 
            label: "Pending",
            icon: "⏳"
        },
        accepted: { 
            color: "bg-blue-50 text-blue-800 border border-blue-200", 
            label: "Accepted",
            icon: "✅"
        },
        rejected: { 
            color: "bg-red-50 text-red-800 border border-red-200", 
            label: "Rejected",
            icon: "❌"
        },
        processing: { 
            color: "bg-purple-50 text-purple-800 border border-purple-200", 
            label: "Processing",
            icon: "⚙️"
        },
        shipped: { 
            color: "bg-indigo-50 text-indigo-800 border border-indigo-200", 
            label: "Shipped",
            icon: "🚚"
        },
        delivered: { 
            color: "bg-emerald-50 text-emerald-800 border border-emerald-200", 
            label: "Delivered",
            icon: "📦"
        },
        cancelled: { 
            color: "bg-red-50 text-red-800 border border-red-200", 
            label: "Cancelled",
            icon: "🚫"
        },
        matched: { 
            color: "bg-teal-50 text-primary border border-primary-light", 
            label: "Matched",
            icon: "🤝"
        }
    };

    const config = statusConfig[status] || { 
        color: "bg-neutral text-gray-800 border border-gray-200", 
        label: status,
        icon: "📄"
    };

    return (
        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${config.color} shadow-sm`}>
            <span className="mr-1.5">{config.icon}</span>
            {config.label}
        </span>
    );
};

// --- Progress Indicator Component ---
const OrderProgress = ({ status }) => {
    const steps = [
        { id: 'pending', label: 'Pending', icon: '⏳' },
        { id: 'accepted', label: 'Accepted', icon: '✅' },
        { id: 'processing', label: 'Processing', icon: '⚙️' },
        { id: 'shipped', label: 'Shipped', icon: '🚚' },
        { id: 'delivered', label: 'Delivered', icon: '📦' }
    ];

    const currentIndex = steps.findIndex(step => step.id === status);
    const activeIndex = currentIndex === -1 ? 0 : currentIndex;

    return (
        <div className="flex items-center justify-between mb-6">
            {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                    <div className={`flex flex-col items-center ${index <= activeIndex ? 'text-primary' : 'text-gray-400'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium border-2 ${
                            index <= activeIndex 
                                ? 'bg-primary-light border-primary text-white shadow-sm' 
                                : 'bg-neutral border-gray-300 text-gray-400'
                        }`}>
                            {step.icon}
                        </div>
                        <span className={`text-xs mt-2 font-medium ${index <= activeIndex ? 'text-primary' : 'text-gray-500'}`}>
                            {step.label}
                        </span>
                    </div>
                    {index < steps.length - 1 && (
                        <div className={`flex-1 h-1 mx-2 ${index < activeIndex ? 'bg-primary' : 'bg-gray-300'} rounded-full`}></div>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

// --- Enhanced Order Card Component ---
const FarmerOrderCard = ({ order, product, onStatusUpdate }) => {
    const [expanded, setExpanded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [timeUntilProcessing, setTimeUntilProcessing] = useState(null);

    const getStatusColor = (status) => {
        const colors = {
            pending: 'border-l-accent',
            accepted: 'border-l-blue-500',
            processing: 'border-l-purple-500',
            shipped: 'border-l-indigo-500',
            delivered: 'border-l-secondary',
            cancelled: 'border-l-red-500',
            matched: 'border-l-primary'
        };
        return colors[status] || 'border-l-gray-500';
    };

    // Calculate time until automatic processing for accepted orders
    useEffect(() => {
        if (order.status === 'accepted' && order.updatedAt) {
            const acceptedTime = new Date(order.updatedAt).getTime();
            const processingTime = acceptedTime + (60 * 1000); // 1 minute later
            const now = new Date().getTime();
            
            if (now < processingTime) {
                const timeLeft = processingTime - now;
                setTimeUntilProcessing(Math.ceil(timeLeft / 1000));
                
                const timer = setInterval(() => {
                    setTimeUntilProcessing(prev => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            // Trigger status update when timer reaches 0
                            if (onStatusUpdate) {
                                onStatusUpdate(order._id, 'processing');
                            }
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
                
                return () => clearInterval(timer);
            } else {
                // If already past processing time, trigger update
                if (onStatusUpdate) {
                    onStatusUpdate(order._id, 'processing');
                }
            }
        }
    }, [order.status, order.updatedAt, order._id, onStatusUpdate]);

    return (
        <div className={`bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border-l-4 ${getStatusColor(order.status)} border border-gray-100 overflow-hidden font-sans`}>
            {/* Order Header */}
            <div className="p-6 cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-start gap-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary-light to-secondary-light rounded-xl flex items-center justify-center border border-gray-200">
                            {product?.images?.[0] && !imageError ? (
                                <img 
                                    src={product.images[0]} 
                                    alt={product.name}
                                    className="w-full h-full object-cover rounded-xl"
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <span className="text-xl text-primary">🌱</span>
                            )}
                        </div>
                    </div>

                    {/* Order Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h3 className="font-heading font-bold text-gray-900 text-lg truncate">
                                    {product?.name || "Product Name Not Available"}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                                    <span className="font-mono bg-neutral px-2 py-1 rounded-md text-xs">
                                        ID: {order._id?.slice(-8) || "N/A"}
                                    </span>
                                    <span>•</span>
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                        {product?.user?.businessName || product?.user?.name || "N/A"}
                                    </span>
                                </p>
                            </div>
                            <div className="text-right ml-4">
                                <div className="text-[16px] font-bold text-primary bg-primary-light/20 px-3 py-2 rounded-lg">
                                    {formatCurrency(order.offeredPrice)}
                                </div>
                                <div className="mt-2">
                                    <OrderStatusBadge status={order.status} className="text-[12px]" />
                                    {order.status === 'accepted' && timeUntilProcessing !== null && timeUntilProcessing > 0 && (
                                        <div className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded-md">
                                            Auto-processing in {timeUntilProcessing}s
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                            <div className="flex items-center text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                {order.quantity} {order.unit || product?.unit || "units"}
                            </div>
                            <div className="flex items-center text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(order.createdAt)}
                            </div>
                            <div className="flex items-center text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {order.status === 'delivered' ? 'Completed' : 'Active'}
                            </div>
                            <div className="flex items-center text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                Updated {formatDate(order.updatedAt)}
                            </div>
                        </div>
                    </div>

                    {/* Expand Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(!expanded);
                        }}
                        className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-neutral hover:bg-gray-200 transition-colors ${
                            expanded ? 'rotate-180' : ''
                        }`}
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Expandable Details */}
            {expanded && (
                <div className="px-6 pb-6 border-t border-gray-100 bg-neutral/50">
                    {/* Progress Bar */}
                    {!['cancelled', 'rejected'].includes(order.status) && (
                        <div className="mb-6">
                            <OrderProgress status={order.status} />
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Product Details */}
                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                            <h4 className="font-subheading font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                Product Details
                            </h4>
                            <div className="grid grid-cols-1 gap-3 text-sm">
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="font-medium text-gray-600">Category</span>
                                    <span className="text-gray-900">{product?.category || "N/A"}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="font-medium text-gray-600">Type</span>
                                    <span className="text-gray-900">{product?.type || "N/A"}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="font-medium text-gray-600">Listed Price</span>
                                    <span className="text-gray-900">{formatCurrency(product?.discountedPrice || product?.price)}</span>
                                </div>
                                <div className="flex justify-between py-2">
                                    <span className="font-medium text-gray-600">Your Offer</span>
                                    <span className="font-semibold text-primary">{formatCurrency(order.offeredPrice)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Order Information */}
                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                            <h4 className="font-subheading font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Order Information
                            </h4>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="font-medium text-gray-600">Status</span>
                                    <div className="text-right">
                                        <OrderStatusBadge status={order.status} />
                                        {order.status === 'accepted' && timeUntilProcessing !== null && timeUntilProcessing > 0 && (
                                            <div className="text-xs text-blue-600 mt-1">
                                                Moving to processing in {timeUntilProcessing}s
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="font-medium text-gray-600">Quantity</span>
                                    <span className="text-gray-900">{order.quantity} {order.unit || product?.unit}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="font-medium text-gray-600">Total Amount</span>
                                    <span className="font-semibold text-primary">{formatCurrency(order.offeredPrice)}</span>
                                </div>
                                <div className="flex justify-between py-2">
                                    <span className="font-medium text-gray-600">Seller ID</span>
                                    <span className="font-mono text-gray-900 bg-neutral px-2 py-1 rounded text-xs">
                                        {product?.user?._id?.slice(-8) || "N/A"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {order.status === 'pending' && (
                        <div className="mt-6 p-4 bg-accent-light/20 rounded-xl border border-accent/30">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h5 className="font-subheading font-semibold text-amber-800 flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        Awaiting Seller Approval
                                    </h5>
                                    <p className="text-sm text-amber-700 mt-1">
                                        Your order is pending seller approval. You can cancel this order if needed.
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        if (window.confirm("Are you sure you want to cancel this order? This action cannot be undone.")) {
                                            // Implement cancel order functionality
                                            console.log("Cancel order:", order._id);
                                        }
                                    }}
                                    className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-all duration-200 font-accent font-medium shadow-sm hover:shadow-md flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Cancel Order
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Enhanced Main Modal Component ---
const FarmerMyOrdersModal = ({ isOpen, onClose }) => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const { token, user, userType } = useAuth();

    // Function to update order status
    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            console.log(`🔄 Updating order ${orderId} to ${newStatus}`);
            
            // Find the order and its product
            const orderToUpdate = orders.find(order => order._id === orderId);
            if (!orderToUpdate) {
                console.error('Order not found for update:', orderId);
                return;
            }

            const productId = orderToUpdate.productId;
            
            // Make API call to update status
            const response = await fetch(`${API_URL}/api/products/${productId}/matches/${orderId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                throw new Error(`Failed to update order status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                // Update local state
                setOrders(prevOrders => 
                    prevOrders.map(order => 
                        order._id === orderId 
                            ? { ...order, status: newStatus, updatedAt: new Date().toISOString() }
                            : order
                    )
                );
                
                console.log(`✅ Order ${orderId} updated to ${newStatus}`);
            }
        } catch (err) {
            console.error('Error updating order status:', err);
        }
    };

    const fetchMyOrders = async () => {
        setLoading(true);
        setError(null);

        try {
            console.log("🔍 Fetching my orders as farmer...");

            // Check if user is authenticated and is a farmer
            if (!token) {
                throw new Error("No authentication token found. Please log in again.");
            }

            if (userType !== 'farmer') {
                throw new Error("This feature is only available for farmers.");
            }

            let currentUserId = null;

            // Method 1: Get user ID from AuthContext (most reliable)
            if (user) {
                currentUserId = user._id || user.id;
                console.log("👤 User ID from AuthContext:", currentUserId, "User object:", user);
            }

            // Method 2: If still no user ID, try to decode from JWT token
            if (!currentUserId && token) {
                try {
                    // Simple JWT decoding (without verification for client-side use)
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    currentUserId = payload.id;
                    console.log("👤 User ID from JWT token:", currentUserId);
                } catch (jwtError) {
                    console.warn("Could not decode JWT token:", jwtError);
                }
            }

            // Method 3: Get user ID from localStorage as fallback
            if (!currentUserId) {
                try {
                    const storedAuth = localStorage.getItem('auth');
                    if (storedAuth) {
                        const parsedAuth = JSON.parse(storedAuth);
                        console.log("💾 Auth data from localStorage:", parsedAuth);
                        
                        // Check different possible locations for user ID
                        if (parsedAuth.user && (parsedAuth.user._id || parsedAuth.user.id)) {
                            currentUserId = parsedAuth.user._id || parsedAuth.user.id;
                        } else if (parsedAuth.userId) {
                            currentUserId = parsedAuth.userId;
                        } else if (parsedAuth._id) {
                            currentUserId = parsedAuth._id;
                        } else if (parsedAuth.id) {
                            currentUserId = parsedAuth.id;
                        }
                        console.log("👤 User ID from localStorage:", currentUserId);
                    }
                } catch (storageError) {
                    console.warn("Could not read localStorage:", storageError);
                }
            }

            if (!currentUserId) {
                // Provide more detailed error information
                console.error("User ID resolution failed:", {
                    hasUser: !!user,
                    userObject: user,
                    hasToken: !!token,
                    localStorageAuth: localStorage.getItem('auth')
                });
                throw new Error("Unable to identify your user account. Please try logging out and back in.");
            }

            console.log("✅ Using user ID:", currentUserId);

            // Rest of your existing code for fetching products and orders...
            const productsResponse = await fetch(`${API_URL}/api/products`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (!productsResponse.ok) {
                throw new Error(`Failed to fetch products: ${productsResponse.status}`);
            }

            const productsData = await productsResponse.json();
            const productsArray = productsData.products || productsData || [];

            console.log("📦 Products found:", productsArray.length);

            // Extract matches where current user is the buyer
            const myOrders = [];
            const productsMap = {};

            productsArray.forEach(product => {
                if (product.matches && Array.isArray(product.matches)) {
                    console.log(`🔍 Checking product "${product.name}" with ${product.matches.length} matches`);
                    
                    product.matches.forEach((match, index) => {
                        try {
                            // Safely get the buyer ID with multiple fallbacks
                            const matchBuyerId = match.buyer?._id || match.buyer?.id || match.buyer;
                            
                            console.log(`   Match ${index + 1}:`, {
                                matchId: match._id,
                                buyerId: matchBuyerId,
                                status: match.status,
                                quantity: match.quantity,
                                offeredPrice: match.offeredPrice
                            });

                            // Check if this match belongs to the current user
                            if (matchBuyerId && currentUserId) {
                                const matchBuyerIdStr = matchBuyerId.toString();
                                const currentUserIdStr = currentUserId.toString();
                                
                                if (matchBuyerIdStr === currentUserIdStr) {
                                    console.log("✅ Found my order!");
                                    myOrders.push({
                                        ...match,
                                        productId: product._id,
                                        productName: product.name,
                                        productCategory: product.category,
                                        productType: product.type,
                                        unit: product.unit
                                    });
                                    productsMap[product._id] = product;
                                }
                            } else {
                                console.log(`   ❌ Match has no valid buyer ID`);
                            }
                        } catch (matchError) {
                            console.error(`Error processing match:`, matchError);
                        }
                    });
                } else {
                    console.log(`📦 Product "${product.name}" has no matches array`);
                }
            });

            console.log("🎉 Total orders found for user:", myOrders.length);
            setOrders(myOrders);
            setProducts(productsMap);

            if (myOrders.length === 0) {
                console.log("ℹ️ No orders found for current user.");
            }

        } catch (err) {
            console.error("Error fetching my orders:", err);
            setError(err.message || "An unexpected error occurred while loading your orders");
            setOrders([]);
            setProducts({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchMyOrders();
        }
    }, [isOpen]);

    // FIXED: Enhanced filtering with search - properly accessing product data
    const filteredOrders = orders.filter(order => {
        const matchesTab = activeTab === "all" || 
            (activeTab === "pending" && ["pending", "matched"].includes(order.status)) ||
            (activeTab === "accepted" && ["accepted"].includes(order.status)) ||
            (activeTab === "processing" && ["processing"].includes(order.status)) ||
            (activeTab === "completed" && ["delivered", "shipped"].includes(order.status)) ||
            (activeTab === "cancelled" && ["cancelled", "rejected"].includes(order.status));

        // FIX: Access product data from the products map using order.productId
        const product = products[order.productId];
        const matchesSearch = searchTerm === "" || 
            (product?.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (order._id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (product?.user?.businessName?.toLowerCase().includes(searchTerm.toLowerCase()));

        return matchesTab && matchesSearch;
    });

    // Enhanced order counts
    const orderCounts = {
        all: orders.length,
        pending: orders.filter(order => ["pending", "matched"].includes(order.status)).length,
        accepted: orders.filter(order => ["accepted"].includes(order.status)).length,
        processing: orders.filter(order => ["processing"].includes(order.status)).length,
        completed: orders.filter(order => ["delivered", "shipped"].includes(order.status)).length,
        cancelled: orders.filter(order => ["cancelled", "rejected"].includes(order.status)).length
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-sans">
            {/* Enhanced Backdrop */}
            <div 
                className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            
            {/* Enhanced Modal Container */}
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden transform transition-all">
                {/* Enhanced Header */}
                <div className="relative bg-gradient-to-r from-primary to-secondary p-8 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="font-heading text-2xl font-bold mb-2">My Orders</h2>
                            <p className="text-primary-light opacity-90 text-[16px] font-subheading">
                                Manage and track your product orders
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-primary-light transition-all p-3 rounded-2xl hover:bg-white/10 backdrop-blur-sm"
                            aria-label="Close modal"
                        >
                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
                        {[
                            { id: "all", label: "Total Orders", count: orderCounts.all, color: "bg-white/20" },
                            { id: "pending", label: "Pending", count: orderCounts.pending, color: "bg-accent/30" },
                            { id: "accepted", label: "Accepted", count: orderCounts.accepted, color: "bg-blue-500/20" },
                            { id: "processing", label: "Processing", count: orderCounts.processing, color: "bg-purple-500/20" },
                            { id: "completed", label: "Completed", count: orderCounts.completed, color: "bg-secondary/30" },
                            { id: "cancelled", label: "Cancelled", count: orderCounts.cancelled, color: "bg-red-500/20" }
                        ].map(stat => (
                            <div 
                                key={stat.id}
                                className={`text-center p-3 rounded-2xl backdrop-blur-sm ${stat.color} border border-white/10 cursor-pointer transition-all hover:scale-105 ${
                                    activeTab === stat.id ? 'ring-2 ring-white ring-opacity-50' : ''
                                }`}
                                onClick={() => setActiveTab(stat.id)}
                            >
                                <div className="text-xl font-bold font-heading">{stat.count}</div>
                                <div className="text-sm text-white/90 font-subheading">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Enhanced Content Area */}
                <div className="p-6 overflow-y-auto max-h-[calc(95vh-200px)] bg-neutral/30">
                    {/* Search and Filter Bar */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Search orders by product name, order ID, or seller..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all font-sans"
                            />
                        </div>
                        <button
                            onClick={fetchMyOrders}
                            className="px-6 py-3 bg-primary text-white rounded-[16px] hover:bg-primary/90 transition-all font-accent font-medium flex items-center gap-2 shadow-sm hover:shadow-md"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh
                        </button>
                    </div>

                    {/* Enhanced Loading State */}
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-sm">
                            <div className="relative">
                                <div className="w-16 h-16 border-4 border-primary-light rounded-full animate-spin"></div>
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 border-4 border-primary rounded-full animate-ping"></div>
                            </div>
                            <span className="mt-4 text-gray-600 text-lg font-medium font-subheading">Loading your orders...</span>
                            <p className="text-gray-500 mt-2 font-sans">We're fetching your latest order information</p>
                        </div>
                    )}

                    {/* Enhanced Error State */}
                    {error && !loading && (
                        <div className="text-center py-16 bg-red-50 rounded-2xl border border-red-200">
                            <div className="text-red-500 text-6xl mb-4">⚠️</div>
                            <div className="text-red-600 text-xl font-semibold font-heading mb-2">Failed to load orders</div>
                            <p className="text-red-500 mb-6 max-w-md mx-auto font-sans">{error}</p>
                            <button
                                onClick={fetchMyOrders}
                                className="bg-red-600 text-white px-8 py-3 rounded-2xl hover:bg-red-700 transition-all font-accent font-medium shadow-sm hover:shadow-md"
                            >
                                Try Again
                            </button>
                        </div>
                    )}

                    {/* Enhanced Empty State */}
                    {!loading && !error && filteredOrders.length === 0 && (
                        <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
                            <h3 className="text-2xl font-semibold text-gray-700 mb-3 font-heading">
                                {searchTerm ? "No matching orders found" : 
                                 activeTab === "all" ? "No orders placed yet" : `No ${activeTab} orders`}
                            </h3>
                            <p className="text-gray-500 text-lg max-w-md mx-auto mb-6 font-sans">
                                {searchTerm 
                                    ? "Try adjusting your search terms or browse different categories."
                                    : activeTab === "all"
                                    ? "Start exploring our marketplace to discover amazing products from local sellers."
                                    : `Your ${activeTab} orders will appear here once available.`
                                }
                            </p>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="bg-primary text-white px-6 py-2.5 rounded-xl hover:bg-primary/90 transition-all font-accent"
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    )}

                    {/* Enhanced Orders List */}
                    {!loading && !error && filteredOrders.length > 0 && (
                        <div className="space-y-4">
                            <div className="text-sm text-gray-500 mb-2 font-sans">
                                Showing {filteredOrders.length} of {orders.length} orders
                            </div>
                            {filteredOrders.map(order => (
                                <FarmerOrderCard
                                    key={order._id}
                                    order={order}
                                    product={products[order.productId]}
                                    onStatusUpdate={updateOrderStatus}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Enhanced Footer */}
                <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-white backdrop-blur-sm">
                    <div className="text-sm text-gray-600 font-sans">
                        Need help? <span className="text-primary font-medium font-accent">Contact support</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="bg-neutral text-gray-700 px-8 py-2.5 rounded-xl hover:bg-gray-300 transition-all font-accent font-medium shadow-sm hover:shadow-md"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FarmerMyOrdersModal;